import React, { useEffect, useMemo, useState } from 'react'
import { CaretDown, CaretRight, Trophy, Medal } from '@phosphor-icons/react'
import { Table } from 'antd'
import { useGetLeagueStandings } from 'api/league/LeagueStanding/LeagueStanding'
import { leagueEventStore } from 'state/league/leagueEvent'
import { StandingByDivision, StandingTeam } from 'types/league/leagueStandings'
import '../../styles/standingstab.scss'

interface TableRow {
  key: string
  isDivisionHeader: boolean
  rank: number | string
  team: string
  profilePhoto: string | null
  gp: number | string
  w: number | string
  l: number | string
  d: number | string
  gf: number | string
  ga: number | string
  gd: number | string
  pts: number | string
  divisionId: string
  divisionGender?: string
  divisionRegion?: string
}

const GENDER_LABEL: Record<string, string> = {
  male: 'Boys',
  female: 'Girls',
  coed: 'Coed',
}

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) return <span className="std-rank std-rank--gold"><Trophy size={12} weight="fill" /> 1</span>
  if (rank === 2) return <span className="std-rank std-rank--silver"><Medal size={12} weight="fill" /> 2</span>
  if (rank === 3) return <span className="std-rank std-rank--bronze"><Medal size={12} weight="fill" /> 3</span>
  return <span className="std-rank">{rank}</span>
}

const TeamCell: React.FC<{ name: string; photo: string | null }> = ({ name, photo }) => {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('')

  return (
    <div className="std-team-cell">
      <div className="std-team-avatar">
        {photo ? (
          <img src={photo} alt={name} className="std-team-avatar-img" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      <span className="std-team-name">{name}</span>
    </div>
  )
}

const StandingsTab: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const { data: standings, isLoading, refetch } = useGetLeagueStandings(leagueId)

  const [collapsedDivisions, setCollapsedDivisions] = useState<Set<string>>(new Set())

  useEffect(() => {
    refetch()
  }, [leagueId, refetch])

  useEffect(() => {
    if (standings?.divisions?.length) {
      const ids = standings.divisions.map((d: StandingByDivision) => String(d.divisionId))
      setCollapsedDivisions(new Set(ids.slice(1)))
    }
  }, [standings])

  const toggleDivision = (divisionId: string) => {
    setCollapsedDivisions(prev => {
      const next = new Set(prev)
      if (next.has(divisionId)) next.delete(divisionId)
      else next.add(divisionId)
      return next
    })
  }

  const tableData = useMemo<TableRow[]>(() => {
    if (!standings?.divisions) return []

    return standings.divisions.flatMap((division: StandingByDivision, di: number) => {
      const header: TableRow = {
        key: `div-${division.divisionId}`,
        isDivisionHeader: true,
        rank: '',
        team: division.division.name,
        profilePhoto: null,
        gp: '', w: '', l: '', d: '', gf: '', ga: '', gd: '', pts: '',
        divisionId: String(division.divisionId),
        divisionGender: division.division.gender,
        divisionRegion: division.division.region,
      }

      const sorted = [...division.leagueTeams].sort((a: StandingTeam, b: StandingTeam) => {
        if (b.points !== a.points) return b.points - a.points
        if (b.goalsDifference !== a.goalsDifference) return b.goalsDifference - a.goalsDifference
        return b.goalsFor - a.goalsFor
      })

      const teamRows: TableRow[] = sorted.map((team: StandingTeam, ti: number) => ({
        key: `team-${di}-${ti}`,
        isDivisionHeader: false,
        rank: ti + 1,
        team: team.name,
        profilePhoto: team.profilePhoto?.thumb || team.profilePhoto?.original || null,
        gp: team.played,
        w: team.wins,
        l: team.losses,
        d: team.draws,
        gf: team.goalsFor,
        ga: team.goalsAgainst,
        gd: team.goalsDifference >= 0 ? `+${team.goalsDifference}` : String(team.goalsDifference),
        pts: team.points,
        divisionId: String(division.divisionId),
      }))

      return [header, ...teamRows]
    })
  }, [standings])

  const visibleData = useMemo(() => {
    return tableData.filter(row => {
      if (row.isDivisionHeader) return true
      return !collapsedDivisions.has(row.divisionId)
    })
  }, [tableData, collapsedDivisions])

  const columns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 56,
      render: (_: any, record: TableRow) => {
        if (record.isDivisionHeader) return null
        return <RankBadge rank={record.rank as number} />
      },
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      render: (_: any, record: TableRow) => {
        if (record.isDivisionHeader) {
          const isCollapsed = collapsedDivisions.has(record.divisionId)
          const genderLabel = GENDER_LABEL[record.divisionGender || ''] || record.divisionGender
          return (
            <div className="std-division-header">
              <div className="std-division-caret">
                {isCollapsed ? <CaretRight size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
              </div>
              <span className="std-division-name">{record.team}</span>
              {genderLabel && <span className="std-division-badge">{genderLabel}</span>}
              {record.divisionRegion && (
                <span className="std-division-region">{record.divisionRegion}</span>
              )}
            </div>
          )
        }
        return <TeamCell name={record.team} photo={record.profilePhoto} />
      },
    },
    {
      title: 'GP',
      dataIndex: 'gp',
      key: 'gp',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat">{record.gp}</span>,
    },
    {
      title: 'W',
      dataIndex: 'w',
      key: 'w',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat std-stat--win">{record.w}</span>,
    },
    {
      title: 'D',
      dataIndex: 'd',
      key: 'd',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat std-stat--draw">{record.d}</span>,
    },
    {
      title: 'L',
      dataIndex: 'l',
      key: 'l',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat std-stat--loss">{record.l}</span>,
    },
    {
      title: 'GF',
      dataIndex: 'gf',
      key: 'gf',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat">{record.gf}</span>,
    },
    {
      title: 'GA',
      dataIndex: 'ga',
      key: 'ga',
      width: 56,
      render: (_: any, record: TableRow) => record.isDivisionHeader ? null : <span className="std-stat">{record.ga}</span>,
    },
    {
      title: 'GD',
      dataIndex: 'gd',
      key: 'gd',
      width: 64,
      render: (_: any, record: TableRow) => {
        if (record.isDivisionHeader) return null
        const val = record.gd
        const isPos = typeof val === 'string' && val.startsWith('+')
        const isNeg = typeof val === 'string' && val.startsWith('-')
        return (
          <span className={`std-stat std-gd ${isPos ? 'std-gd--pos' : isNeg ? 'std-gd--neg' : ''}`}>
            {val}
          </span>
        )
      },
    },
    {
      title: 'Pts',
      dataIndex: 'pts',
      key: 'pts',
      width: 64,
      render: (_: any, record: TableRow) =>
        record.isDivisionHeader ? null : <span className="std-stat std-pts">{record.pts}</span>,
    },
  ]

  const hasDivisions = standings?.divisions && standings.divisions.length > 0

  return (
    <div className="std-container">
      {/* Header */}
      <div className="std-header">
        <div>
          <h2 className="std-title">Standings</h2>
          <p className="std-subtitle">League table sorted by points, goal difference, then goals scored.</p>
        </div>

        {/* Points legend */}
        {standings && (
          <div className="std-points-legend">
            <div className="std-legend-item std-legend-item--win">
              <span className="std-legend-label">Win</span>
              <span className="std-legend-pts">{standings.pointsPerWin} pts</span>
            </div>
            <div className="std-legend-item std-legend-item--draw">
              <span className="std-legend-label">Draw</span>
              <span className="std-legend-pts">{standings.pointsPerDraw} pts</span>
            </div>
            <div className="std-legend-item std-legend-item--loss">
              <span className="std-legend-label">Loss</span>
              <span className="std-legend-pts">{standings.pointsPerLoss} pts</span>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="std-table-wrap">
        {isLoading ? (
          <div className="std-loading">
            <p>Loading standings…</p>
          </div>
        ) : !hasDivisions ? (
          <div className="std-empty">
            <Trophy size={40} color="#d0d0d0" />
            <p>No standings yet</p>
            <span>Standings will appear once games have been played.</span>
          </div>
        ) : (
          <Table
            className="colored_header_table std-table"
            columns={columns}
            dataSource={visibleData}
            pagination={false}
            scroll={{ x: 700 }}
            rowClassName={record =>
              record.isDivisionHeader ? 'std-division-row' : 'std-team-row'
            }
            onRow={record => ({
              onClick: () => {
                if (record.isDivisionHeader) toggleDivision(String(record.divisionId))
              },
            })}
          />
        )}
      </div>
    </div>
  )
}

export default StandingsTab
